import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import GroupIcon from "@mui/icons-material/Group";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../layout/DashboardLayout";
import { brand } from "../../theme";
import { uploadProfilePicture, getProfileData } from "../../api/auth";
import { toast } from "react-toastify";

function SectionLabel({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        color: "#94a3b8",
        textTransform: "uppercase",
        letterSpacing: 1,
        fontWeight: 600,
        display: "block",
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="#94a3b8" display="block" mb={0.25}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} color="#0f172a">
        {value}
      </Typography>
    </Box>
  );
}

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getProfileData();
        setProfile(res.data);
      } catch (err) {
        console.error("Profile fetch error:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await uploadProfilePicture(formData);
      setRefresh(!refresh);
      toast.success("Profile picture updated.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to upload profile picture. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  const agency = profile?.agency || {};
  const role = user?.role;
  const displayName = agency.name || user?.name || user?.email?.split("@")[0];
  const initials = displayName
    ?.split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const roleLabel =
    role === "agency_owner"
      ? "Agency Owner"
      : role === "agency_staff"
        ? "Agency Staff"
        : role?.replace(/_/g, " ") || "User";

  return (
    <DashboardLayout>
      <Box>
        {/* Hero profile card */}
        <Card sx={{ mb: 3, overflow: "hidden" }}>
          {/* Gradient banner */}
          <Box
            sx={{
              height: 88,
              background: brand.black,
            }}
          />
          <CardContent sx={{ px: 3, pt: 0, pb: 3 }}>
            <Box
              display="flex"
              alignItems="flex-end"
              gap={2.5}
              mt="-44px"
              mb={2}
            >
              {/* Avatar with upload overlay */}
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Avatar
                  src={profile?.user?.avatarUrl || ""}
                  sx={{
                    width: 84,
                    height: 84,
                    fontSize: "1.75rem",
                    border: "3px solid #fff",
                    boxShadow: "0 4px 12px rgba(15,23,42,0.15)",
                  }}
                >
                  {initials}
                </Avatar>
                <Tooltip title="Change picture">
                  <IconButton
                    component="label"
                    disabled={uploading}
                    size="small"
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      bgcolor: brand.terracotta,
                      color: "#fff",
                      width: 26,
                      height: 26,
                      border: "2px solid #fff",
                      "&:hover": { bgcolor: brand.terracottaDark },
                    }}
                  >
                    {uploading ? (
                      <CircularProgress size={14} sx={{ color: "#fff" }} />
                    ) : (
                      <CameraAltIcon sx={{ fontSize: "0.9rem" }} />
                    )}
                    <input
                      type="file"
                      hidden
                      onChange={handleProfilePicUpload}
                    />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box pb={0.5}>
                <Typography variant="h6" color="#ffffff">
                  {displayName}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Chip
                    label={roleLabel}
                    size="small"
                    sx={{
                      bgcolor: "#ede9fe",
                      color: "#6d28d9",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <InfoRow label="Email" value={user?.email} />
              </Grid>
              {agency.email && agency.email !== user?.email && (
                <Grid item xs={12} sm={6} md={3}>
                  <InfoRow label="Agency Email" value={agency.email} />
                </Grid>
              )}
              {agency.name && (
                <Grid item xs={12} sm={6} md={3}>
                  <InfoRow label="Agency Name" value={agency.name} />
                </Grid>
              )}
              {agency.phone && (
                <Grid item xs={12} sm={6} md={3}>
                  <InfoRow label="Phone" value={agency.phone} />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Your Sales/Account Specialist */}
        {agency.assignedSpecialist && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <SupportAgentIcon sx={{ color: brand.terracotta, fontSize: "1.1rem" }} />
                <SectionLabel>Your Sales/Account Specialist</SectionLabel>
              </Box>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={4}>
                  <InfoRow label="Name" value={agency.assignedSpecialist.name} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="caption" color="#94a3b8" display="block" mb={0.25}>
                      Email
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <EmailIcon sx={{ fontSize: "0.95rem", color: "#94a3b8" }} />
                      <Typography
                        component="a"
                        href={`mailto:${agency.assignedSpecialist.email}`}
                        variant="body2"
                        fontWeight={500}
                        color={brand.terracotta}
                        sx={{ textDecoration: "none" }}
                      >
                        {agency.assignedSpecialist.email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {agency.assignedSpecialist.phone && (
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="caption" color="#94a3b8" display="block" mb={0.25}>
                        Phone
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PhoneIcon sx={{ fontSize: "0.95rem", color: "#94a3b8" }} />
                        <Typography variant="body2" fontWeight={500} color="#0f172a">
                          {agency.assignedSpecialist.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Referral Program — coming soon */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <CardGiftcardIcon
                  sx={{ color: brand.terracotta, fontSize: "1.1rem" }}
                />
                <SectionLabel>Referral Program</SectionLabel>
              </Box>
              <Chip
                label="Coming Soon"
                size="small"
                sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 600 }}
              />
            </Box>
            <Typography variant="body2" color="#64748b">
              Generate your unique referral code and earn points when new
              agencies register using it. This feature is launching soon.
            </Typography>
          </CardContent>
        </Card>

        {/* Agency Staff — owner only, coming soon */}
        {role === "agency_owner" && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={0.5}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <GroupIcon
                    sx={{ color: brand.terracotta, fontSize: "1.1rem" }}
                  />
                  <SectionLabel>Agency Staff</SectionLabel>
                </Box>
                <Chip
                  label="Coming Soon"
                  size="small"
                  sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 600 }}
                />
              </Box>

              <Typography variant="body2" color="#64748b">
                Adding staff members to your agency account is launching soon.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </DashboardLayout>
  );
}
